import { useEffect, useCallback, useRef } from 'react';

const VERSION_STORAGE_KEY = 'cashee_version_buildTime';
const CHECK_INTERVAL = 30000; // 30 seconds

interface VersionInfo {
  version: string;
  buildTime: string;
  hash: string;
}

export function useVersionCheck() {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingRef = useRef(false);

  const fetchVersion = useCallback(async (): Promise<VersionInfo | null> => {
    try {
      // Cache-busting: add timestamp to bypass all caches
      const response = await fetch(`/version.json?_=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
      });

      if (!response.ok) {
        console.log('[Version] Failed to fetch version.json:', response.status);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.log('[Version] Error fetching version:', error);
      return null;
    }
  }, []);

  const checkForUpdates = useCallback(async () => {
    // Prevent concurrent checks
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;

    try {
      const remoteVersion = await fetchVersion();
      if (!remoteVersion) {
        isCheckingRef.current = false;
        return;
      }

      const storedBuildTime = localStorage.getItem(VERSION_STORAGE_KEY);

      // First time: store current version
      if (!storedBuildTime) {
        localStorage.setItem(VERSION_STORAGE_KEY, remoteVersion.buildTime);
        console.log('[Version] Initial version stored:', remoteVersion.version);
        isCheckingRef.current = false;
        return;
      }

      // Compare build times
      const remoteTime = new Date(remoteVersion.buildTime).getTime();
      const localTime = new Date(storedBuildTime).getTime();

      if (remoteTime > localTime) {
        console.log('[Version] New version available!', {
          current: storedBuildTime,
          new: remoteVersion.buildTime,
        });
        // Dispatch event for UpdateNotification to catch
        window.dispatchEvent(new CustomEvent('sw:update-available'));
      }
    } catch (error) {
      console.error('[Version] Check error:', error);
    } finally {
      isCheckingRef.current = false;
    }
  }, [fetchVersion]);

  const forceCheck = useCallback(async (): Promise<boolean> => {
    const remoteVersion = await fetchVersion();
    if (!remoteVersion) return false;

    const storedBuildTime = localStorage.getItem(VERSION_STORAGE_KEY);
    if (!storedBuildTime) {
      localStorage.setItem(VERSION_STORAGE_KEY, remoteVersion.buildTime);
      return false;
    }

    const remoteTime = new Date(remoteVersion.buildTime).getTime();
    const localTime = new Date(storedBuildTime).getTime();

    if (remoteTime > localTime) {
      window.dispatchEvent(new CustomEvent('sw:update-available'));
      return true;
    }

    return false;
  }, [fetchVersion]);

  // Store version on successful update/reload
  const markVersionAsCurrent = useCallback(async () => {
    const remoteVersion = await fetchVersion();
    if (remoteVersion) {
      localStorage.setItem(VERSION_STORAGE_KEY, remoteVersion.buildTime);
    }
  }, [fetchVersion]);

  useEffect(() => {
    // Initial check on mount (delayed to not block initial render)
    const initialTimeout = setTimeout(() => {
      checkForUpdates();
    }, 3000);

    // Periodic polling
    intervalRef.current = setInterval(checkForUpdates, CHECK_INTERVAL);

    // Visibility change handler (iOS PWA comes back from background)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[Version] App visible, checking for updates...');
        checkForUpdates();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also check on focus (additional iOS coverage)
    const handleFocus = () => {
      console.log('[Version] Window focused, checking for updates...');
      checkForUpdates();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkForUpdates]);

  return { forceCheck, markVersionAsCurrent };
}
